<script setup lang="ts">
import { ref, computed, watch, onMounted, inject, nextTick } from 'vue';
import type { Ref } from 'vue';
import TimeSelect from '../../TimeSelect/TimeSelect.vue';
import { DtRadioGroup } from '@dialpad/dialtone/vue3/lib/radio-group';
import { DtRadio } from '@dialpad/dialtone/vue3/lib/radio';
import { DtValidationMessages } from '@dialpad/dialtone/vue3/lib/validation-messages';
import type { Campaign } from '@/apis/generated/dialbunny';
import { useCampaignStore } from '@/stores';
import { roundToNearestHours, addHours } from 'date-fns';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';
import { DATA_QA } from './start-and-end-time.constants';
import logger from '@/logger';
import { CampaignAction, ValidationError } from '../campaign-edit.constants';
import { SearchDropdownError } from '@/app/components/SearchDropdown/search-dropdown.constants';
import { showCampaignSyncToast } from '@/campaign/utils/sync-toast';

const log = logger.getChild('StartAndEndTime');
log.debug('Mounting');
onMounted(() => {
  log.debug('Mounted');
});

const scheduledStartTimeErrorMapping = new Map<
  SearchDropdownError,
  ValidationError
>([
  [SearchDropdownError.REQUIRED, ValidationError.MISSING_START_TIME],
  [SearchDropdownError.MIN_VALUE, ValidationError.MIN_START_TIME],
  [SearchDropdownError.MAX_VALUE, ValidationError.MAX_START_TIME],
]);

const scheduledEndTimeErrorMapping = new Map<
  SearchDropdownError,
  ValidationError
>([
  [SearchDropdownError.REQUIRED, ValidationError.MISSING_END_TIME],
  [SearchDropdownError.MIN_VALUE, ValidationError.MIN_END_TIME],
]);

const isSyncError = ref(false);

type Props = {
  campaign: Campaign;
  showEndTime?: boolean;
  showSectionTitle?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  showEndTime: true,
  showSectionTitle: true,
});

const campaignStore = useCampaignStore();

const emit =
  defineEmits<(e: 'update:error', value: ValidationError[]) => void>();

const tempCampaignAction = ref<CampaignAction | null>(null);
const tempScheduledStartTime = ref<Date | null>(null);
const tempScheduledEndTime = ref<Date | null>(null);
const currentTime = ref<Date>(new Date());
const scheduledStartTimeError = ref<ValidationError | null>(null);
const scheduledEndTimeError = ref<ValidationError | null>(null);

const campaignAction = computed(() => {
  return tempCampaignAction.value ?? props.campaign.dateScheduledStart
    ? CampaignAction.SCHEDULE
    : CampaignAction.LAUNCH;
});

const scheduledStartTime = computed(() => {
  return (
    tempScheduledStartTime.value ??
    (props.campaign.dateScheduledStart
      ? new Date(props.campaign.dateScheduledStart)
      : null)
  );
});

const scheduledEndTime = computed(() => {
  return (
    tempScheduledEndTime.value ??
    (props.campaign.dateScheduledEnd
      ? new Date(props.campaign.dateScheduledEnd)
      : null)
  );
});

const scheduledStartTimeStartOn = computed(() =>
  roundToNearestHours(currentTime.value, { roundingMethod: 'ceil' }),
);

const scheduledStartTimeEndOn = computed(() =>
  addHours(scheduledStartTimeStartOn.value, 23),
);

const scheduledEndTimeStartOn = computed(() => {
  const date = scheduledStartTime.value
    ? addHours(scheduledStartTime.value, 1)
    : currentTime.value;
  return roundToNearestHours(date, { roundingMethod: 'ceil' });
});

const scheduledEndTimeEndOn = computed(() =>
  addHours(scheduledEndTimeStartOn.value, 23),
);

const updateCurrentTime = () => {
  currentTime.value = new Date();
};

const validationTrigger = inject<Ref<number>>(TRIGGER_FORM_VALIDATION, ref(0));

watch([validationTrigger], () => {
  log.debug('Validation trigger');
  updateCurrentTime();
});

const updateInProgress = ref(false);

const onCampaignActionChanged = async (action: CampaignAction) => {
  tempCampaignAction.value = action;
  tempScheduledStartTime.value =
    action === CampaignAction.SCHEDULE
      ? roundToNearestHours(new Date(), {
          roundingMethod: 'ceil',
        })
      : null;
  if (action === CampaignAction.SCHEDULE) {
    await nextTick();
    updateCurrentTime();
  } else {
    scheduledStartTimeError.value = null;
  }
  updateInProgress.value = true;
  campaignStore.update(
    props.campaign.id,
    {
      dateScheduledStart: tempScheduledStartTime.value,
    },
    {
      onSuccess: () => {
        void showCampaignSyncToast(true);
        tempCampaignAction.value = null;
        tempScheduledStartTime.value = null;
        isSyncError.value = false;
        updateInProgress.value = false;
      },
      onRetry: () => {
        void showCampaignSyncToast(false);
      },
      onError: (error) => {
        log.log(
          `Error updating campaign ${props.campaign.id} start option to ${action}`,
          error,
        );
        isSyncError.value = true;
        updateInProgress.value = false;
      },
    },
  );
};

const updateScheduledStartTime = async (value: Date | null | undefined) => {
  if (!value) return;
  tempScheduledStartTime.value = value;
  updateInProgress.value = true;
  campaignStore.update(
    props.campaign.id,
    {
      dateScheduledStart: tempScheduledStartTime.value,
    },
    {
      onSuccess: () => {
        void showCampaignSyncToast(true);
        tempScheduledStartTime.value = null;
        isSyncError.value = false;
        updateInProgress.value = false;
      },
      onRetry: () => {
        void showCampaignSyncToast(false);
      },
      onError: (error) => {
        log.log(
          `Error updating campaign ${props.campaign.id} scheduled start time`,
          error,
        );
        isSyncError.value = true;
        updateInProgress.value = false;
      },
    },
  );
};

const updateScheduledEndTime = (value: Date | null | undefined) => {
  if (!value) return;
  tempScheduledEndTime.value = value;
  updateInProgress.value = true;
  campaignStore.update(
    props.campaign.id,
    {
      dateScheduledEnd: tempScheduledEndTime.value,
    },
    {
      onSuccess: () => {
        void showCampaignSyncToast(true);
        tempScheduledEndTime.value = null;
        isSyncError.value = false;
        updateInProgress.value = false;
      },
      onRetry: () => {
        void showCampaignSyncToast(false);
      },
      onError: (error) => {
        log.log(
          `Error updating campaign ${props.campaign.id} scheduled end time`,
          error,
        );
        isSyncError.value = true;
        updateInProgress.value = false;
      },
    },
  );
};

const onScheduledStartTimeError = (value: SearchDropdownError | null) => {
  if (!value) {
    scheduledStartTimeError.value = null;
    return;
  }
  scheduledStartTimeError.value =
    scheduledStartTimeErrorMapping.get(value) ?? null;
};

const onScheduledEndTimeError = (value: SearchDropdownError | null) => {
  if (!value) {
    scheduledEndTimeError.value = null;
    return;
  }
  scheduledEndTimeError.value = scheduledEndTimeErrorMapping.get(value) ?? null;
};

watch([scheduledStartTimeError, scheduledEndTimeError, isSyncError], () => {
  emit('update:error', [
    ...(scheduledStartTimeError.value ? [scheduledStartTimeError.value] : []),
    ...(scheduledEndTimeError.value ? [scheduledEndTimeError.value] : []),
    ...(isSyncError.value ? [ValidationError.SYNC_FAILED] : []),
  ]);
});
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <p v-if="showSectionTitle" class="d-fs-300">
      {{ $t('CAMPAIGN_EDIT_START_AND_END_TIME_TITLE') }}
    </p>
    <div :data-qa="DATA_QA.START_TIME_SELECT" class="d-mt16">
      <p
        v-if="showSectionTitle"
        class="d-fc-secondary d-fw-semibold d-lh-300 d-mb8"
      >
        {{ $t('CAMPAIGN_EDIT_START_AND_END_TIME_START_LABEL') }}
      </p>
      <DtRadioGroup
        :model-value="campaignAction"
        :disabled="updateInProgress"
        :name="`start-options-radio-group-${Date.now()}`"
        :aria-label="
          $t('CAMPAIGN_EDIT_START_AND_END_TIME_START_OPTIONS_ARIA_LABEL')
        "
        @input="onCampaignActionChanged"
      >
        <DtRadio :data-qa="DATA_QA.LAUNCH_NOW" :value="CampaignAction.LAUNCH">
          {{ $t('CAMPAIGN_EDIT_START_AND_END_TIME_LAUNCH_OPTION') }}
        </DtRadio>
        <DtRadio
          :data-qa="DATA_QA.SCHEDULED_START"
          :value="CampaignAction.SCHEDULE"
        >
          {{ $t('CAMPAIGN_EDIT_START_AND_END_TIME_SCHEDULE_OPTION') }}
        </DtRadio>
      </DtRadioGroup>
      <div
        v-if="campaignAction === CampaignAction.SCHEDULE"
        class="d-mt4 d-pl24"
      >
        <TimeSelect
          :disabled="updateInProgress"
          :model-value="scheduledStartTime"
          :show-timezone="true"
          :start-on="scheduledStartTimeStartOn"
          :end-on="scheduledStartTimeEndOn"
          :required="true"
          :min-time="currentTime"
          :max-time="scheduledEndTime ? addHours(scheduledEndTime, -1) : null"
          :error-messages="
            $ta('CAMPAIGN_EDIT_START_AND_END_TIME_START_TIME_ERROR_MESSAGES')
          "
          class="d-w216"
          @update:model-value="updateScheduledStartTime"
          @update:opened="(isOpen) => isOpen && updateCurrentTime()"
          @update:error="onScheduledStartTimeError"
        />
      </div>
    </div>
    <div v-if="showEndTime" :data-qa="DATA_QA.END_TIME_SELECT" class="d-mt24">
      <p
        v-if="showSectionTitle"
        class="d-fc-secondary d-fw-semibold d-lh-300 d-mb8"
      >
        {{ $t('CAMPAIGN_EDIT_START_AND_END_TIME_END_LABEL') }}
      </p>
      <TimeSelect
        :disabled="updateInProgress"
        :model-value="scheduledEndTime"
        :show-timezone="true"
        :start-on="scheduledEndTimeStartOn"
        :end-on="scheduledEndTimeEndOn"
        :required="true"
        :min-time="currentTime"
        :error-messages="
          $ta('CAMPAIGN_EDIT_START_AND_END_TIME_END_TIME_ERROR_MESSAGES')
        "
        class="d-w216"
        @update:model-value="updateScheduledEndTime"
        @update:opened="(isOpen) => isOpen && updateCurrentTime()"
        @update:error="onScheduledEndTimeError"
      />
    </div>
    <DtValidationMessages
      v-if="isSyncError"
      :validation-messages="[
        {
          message: $ta(
            `CAMPAIGN_EDIT_START_AND_END_TIME_GENERAL_ERROR_MESSAGES`,
          )[ValidationError.SYNC_FAILED],
          type: 'error',
        },
      ]"
    />
  </div>
</template>
